import { useState, useEffect } from 'react';
import { LoginItem } from '../../components/Login';
import Modal from 'react-modal';
import { SignUpItem } from '../../components/SignupButton';

const MAX_SEARCHES = 20; // Maximum number of searches allowed for logged-out users
const STORAGE_KEY = 'logged_out_search_count';

// Add styles for the buttons
const buttonStyles = `
  .theme-button {
    padding: 0.5rem 1rem;
    border-radius: 0.375rem;
    border: 1px solid var(--rhea-card-border);
    background: var(--rhea-card-bg);
    color: var(--rhea-text-secondary);
    cursor: pointer;
    transition: background-color 0.2s;
  }
  .theme-button:hover {
    background: var(--rhea-card-hover);
  }
`;

export const useLoggedOutUserSearchCounter = (loggedIn: boolean) => {
  const [searchCount, setSearchCount] = useState(0);
  const [showLimitReached, setShowLimitReached] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    // Load the search count from localStorage on mount
    const storedCount = localStorage.getItem(STORAGE_KEY);
    if (storedCount) {
      const count = parseInt(storedCount, 10);
      setSearchCount(count);
      if (count >= MAX_SEARCHES && !loggedIn) {
        setShowLimitReached(true);
      }
    }
    setIsInitialized(true);
  }, []);

  const incrementSearchCount = () => {
    setSearchCount(prevCount => {
      const newCount = prevCount + 1;
      localStorage.setItem(STORAGE_KEY, newCount.toString());
      
      if (newCount >= MAX_SEARCHES && !loggedIn) {
        setShowLimitReached(true);
      }
      return newCount;
    });
  };

  const resetSearchCount = () => {
    setSearchCount(0);
    localStorage.removeItem(STORAGE_KEY);
  };

  const LimitReachedMessage = () => (
    <>
      <style>{buttonStyles}</style>
      <Modal
        isOpen={showLimitReached}
        onRequestClose={() => setShowLimitReached(false)}
        style={{
          overlay: {
            background: 'rgba(0, 0, 0, 0.75)',
            zIndex: 100,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          },
          content: {
            border: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 'fit-content',
            height: 'auto',
            inset: 'auto',
            background: 'transparent',
            padding: '1rem',
            zIndex: 1000
          }
        }}
      >
        <div 
          className="rounded-lg p-6 max-w-md w-full mx-4"
          style={{
            background: 'var(--rhea-bg-primary)',
            border: '1px solid var(--rhea-card-border)',
          }}
        >
          <h2 
            className="text-xl font-semibold mb-4"
            style={{ color: 'var(--rhea-text-primary)' }}
          >
            Search Limit Reached
          </h2>
          <p 
            className="mb-4"
            style={{ color: 'var(--rhea-text-secondary)' }}
          >
            Thank you for your interest in Rhea! We’re happy to give you more bandwidth, just sign up and make an account.
            <br />
            <br />
            If you’re in need of support, please reach out to rens@thoughtful-oasis.com
          </p>
          <div className="flex justify-end gap-4">
            <button
              className="theme-button"
              onClick={() => setShowLimitReached(false)}
            >
              Close
            </button>
            <div 
              className="flex items-center gap-2"
              style={{ color: 'var(--rhea-text-primary)' }}
            >
              <LoginItem 
                title="Login to ThoughtfulOasis" 
                className="theme-button"
              />
              <SignUpItem 
                title="Register with ThoughtfulOasis" 
                className="theme-button"
              />
            </div>
          </div>
        </div>
      </Modal>
    </>
  );

  return {
    searchCount,
    incrementSearchCount,
    resetSearchCount,
    LimitReachedMessage,
    hasReachedLimit: searchCount >= MAX_SEARCHES,
    showLimitReached,
    setShowLimitReached,
    isInitialized,
  };
};
